import React, { useState } from 'react';
import { X, Undo2, AlertCircle } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  payment: { transactionId: string; amount: number };
  onClose: () => void;
  onVoided: () => void;
}

const VoidModal: React.FC<Props> = ({ payment, onClose, onVoided }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = reason.trim().length >= 3;

  const handleVoid = async () => {
    if (!isValid) return;
    try {
      setBusy(true);
      setError(null);
      await api.posPayments.void({ transactionId: payment.transactionId, reason: reason.trim() });
      toastService.success('Transaction voided');
      onVoided();
    } catch (err: any) {
      setError(err?.message || 'Void failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="flex items-center gap-3 p-5 border-b border-stone-100">
          <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
            <Undo2 size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-stone-900 font-semibold text-base">Void transaction</h2>
            <p className="text-stone-500 text-xs mt-0.5 truncate font-mono">{payment.transactionId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-800">
              <span className="font-medium">Void removes the authorisation entirely.</span> Only possible before batch close. If this transaction is already settled, use Refund instead.
            </p>
          </div>

          <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-500">Amount</span>
              <span className="font-mono text-stone-800 font-medium">${payment.amount.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-700 mb-1">Reason</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Wrong card presented, cashier error, etc."
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-stone-100 bg-stone-50 rounded-b-2xl flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800">Cancel</button>
          <button
            onClick={handleVoid}
            disabled={!isValid || busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <Undo2 size={14} />}
            {busy ? 'Voiding…' : 'Void transaction'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoidModal;
